"use client";

import { useGesture } from "@use-gesture/react";
import Image from "next/image";
import * as React from "react";

interface PinchZoomImageProps {
	src: string;
	alt: string;
	sizes?: string;
	quality?: number;
	priority?: boolean;
	fetchPriority?: "high" | "low";
	loading?: "eager" | "lazy";
	onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

/**
 * PinchZoomImage - Enables pinch-to-zoom for images (specifically floor plans)
 * Uses @use-gesture/react for lightweight, reliable pinch-to-zoom (only 8.9KB gzipped!)
 */
export function PinchZoomImage({
	src,
	alt,
	sizes,
	quality,
	priority,
	fetchPriority,
	loading,
	onError,
}: PinchZoomImageProps) {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [style, setStyle] = React.useState({
		x: 0,
		y: 0,
		scale: 1,
	});

	// Use gesture hook for pinch-to-zoom, drag-to-pan, and double-tap
	useGesture(
		{
			onPinch: ({ offset: [scale], origin: [ox, oy], first, memo, event }) => {
				// Prevent Embla from seeing this gesture
				event.stopPropagation();

				// Clamp scale between 1 and 5
				const clampedScale = Math.max(1, Math.min(5, scale));

				if (first) {
					// Store the initial state when pinch starts
					const container = containerRef.current;
					if (!container) return;

					const rect = container.getBoundingClientRect();
					const relativeX = ox - rect.left - rect.width / 2;
					const relativeY = oy - rect.top - rect.height / 2;

					return {
						initialX: style.x,
						initialY: style.y,
						initialScale: style.scale,
						originX: relativeX,
						originY: relativeY,
					};
				}

				if (!memo) return;

				// Calculate the scale change
				const scaleChange = clampedScale / memo.initialScale;

				// Adjust position to zoom towards pinch origin
				const newX = memo.initialX - memo.originX * (scaleChange - 1);
				const newY = memo.initialY - memo.originY * (scaleChange - 1);

				setStyle({
					x: newX,
					y: newY,
					scale: clampedScale,
				});

				return memo;
			},
			onDrag: ({ offset: [x, y], pinching, tap, event }) => {
				if (tap) return;
				if (pinching) return;

				// If zoomed in, stop propagation so Embla doesn't swipe
				if (style.scale > 1) {
					event.stopPropagation();
				} else {
					// If not zoomed, let Embla handle the swipe
					return;
				}

				const container = containerRef.current;
				if (!container) return;

				const { width, height } = container.getBoundingClientRect();
				// Maximum translation so edges never go empty
				const maxX = (width * (style.scale - 1)) / 2;
				const maxY = (height * (style.scale - 1)) / 2;

				const clampedX = Math.min(maxX, Math.max(-maxX, x));
				const clampedY = Math.min(maxY, Math.max(-maxY, y));

				setStyle(prev => ({
					...prev,
					x: clampedX,
					y: clampedY,
				}));
			},
		},
		{
			target: containerRef,
			eventOptions: { passive: false },
			pinch: {
				scaleBounds: { min: 1, max: 5 },
				rubberband: true,
			},
			drag: {
				from: () => [style.x, style.y],
				filterTaps: true,
			},
		},
	);

	// Double-tap to zoom in/out
	const lastTapRef = React.useRef<number>(0);
	const handleDoubleTap = React.useCallback(
		(e: React.MouseEvent | React.TouchEvent) => {
			const now = Date.now();
			const timeSinceLastTap = now - lastTapRef.current;

			if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
				e.preventDefault();
				const container = containerRef.current;
				if (!container) return;

				const rect = container.getBoundingClientRect();
				const [clientX, clientY] =
					"changedTouches" in e ? [e.changedTouches[0].clientX, e.changedTouches[0].clientY] : [e.clientX, e.clientY];

				const tapX = clientX - rect.left - rect.width / 2;
				const tapY = clientY - rect.top - rect.height / 2;

				if (style.scale > 1) {
					setStyle({ x: 0, y: 0, scale: 1 });
				} else {
					const targetScale = 2.5;
					const newX = -tapX * (targetScale - 1);
					const newY = -tapY * (targetScale - 1);
					setStyle({ x: newX, y: newY, scale: targetScale });
				}
				lastTapRef.current = 0; // Reset to prevent triple-tap
			} else {
				lastTapRef.current = now;
			}
		},
		[style.scale],
	);

	return (
		<div
			ref={containerRef}
			className="absolute inset-0 w-full h-full overflow-hidden touch-none"
			onClick={handleDoubleTap}
			style={{
				cursor: style.scale > 1 ? "grab" : "default",
			}}
		>
			<div
				style={{
					transform: `translate(${style.x}px, ${style.y}px) scale(${style.scale})`,
					transformOrigin: "center center",
					width: "100%",
					height: "100%",
					position: "relative",
					transition: style.scale === 1 || style.scale === 2.5 ? "transform 0.3s ease-out" : "none",
				}}
			>
				<Image
					src={src}
					alt={alt}
					fill
					className="object-contain select-none pointer-events-none"
					draggable={false}
					sizes={sizes || "100vw"}
					quality={quality}
					priority={priority}
					fetchPriority={fetchPriority}
					loading={loading}
					onError={onError}
				/>
			</div>
		</div>
	);
}
